import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { SEO } from "../components/SEO";
import { API, LOGO_MARK } from "../lib/site";
import { HOTEL_TOKEN_KEY } from "./Join";

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const HotelLogin = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/hotels/login`, { email, password });
            localStorage.setItem(HOTEL_TOKEN_KEY, data.token);
            navigate("/hotel/dashboard");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main>
            <SEO title="Hotel Login | Global Green Hotel" description="Sign in to your GGH hotel dashboard." />
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5 py-32">
                <form
                    onSubmit={submit}
                    data-testid="hotel-login-form"
                    className="w-full max-w-md rounded-2xl border border-white/10 bg-[#07211A] p-9"
                >
                    <img src={LOGO_MARK} alt="Global Green Hotel logo" className="w-36" />
                    <h1 className="font-display mt-7 text-3xl font-medium text-[#FDFBF7]">Hotel Login</h1>
                    <p className="mt-2 text-sm text-[#FDFBF7]/60">
                        Access your GGH sustainability dashboard.
                    </p>
                    <label htmlFor="hl-email" className="mt-8 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                        Business Email
                    </label>
                    <input
                        id="hl-email"
                        data-testid="hotel-login-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-field"
                        placeholder="you@yourhotel.com"
                        autoComplete="username"
                    />
                    <label htmlFor="hl-password" className="mt-5 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                        Password
                    </label>
                    <input
                        id="hl-password"
                        data-testid="hotel-login-password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-field"
                        placeholder="••••••••"
                        autoComplete="current-password"
                    />
                    {error && (
                        <p data-testid="hotel-login-error" role="alert" className="mt-4 text-sm font-semibold text-[#E8A0A0]">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        data-testid="hotel-login-submit"
                        disabled={busy}
                        className="btn-gold mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {busy ? "Signing in…" : "Sign In"}
                    </button>
                    <p className="mt-4 text-center text-xs text-[#FDFBF7]/50">
                        <Link to="/forgot-password" data-testid="hotel-login-forgot" className="font-bold text-[#D9B96A] hover:underline">
                            Forgot password?
                        </Link>
                    </p>
                    <p className="mt-6 text-center text-xs text-[#FDFBF7]/50">
                        No account yet?{" "}
                        <Link to="/join" data-testid="hotel-login-join" className="font-bold text-[#D9B96A] hover:underline">
                            Join GGH
                        </Link>
                    </p>
                </form>
            </div>
        </main>
    );
};

export default HotelLogin;
